import type { NodeRecord } from '@flobi/artwork-core';
import { useMemo, useState, type ReactNode } from 'react';
import { NumberField, Slider } from '../../ui/controls';
import { Menu } from '../../ui/menu';
import { useAnchor } from '../../ui/popover';
import { tip } from '../../ui/tooltip';
import { RECIPES } from '../tools/effectors';

/**
 * Repeat, from the Edit dock: one popover for the arrangement, its numbers and the
 * variations — Tarek's pick A of six (2026-10-01, https://claude.ai/artifact/XHBMutSiz1FuEDxxrAZP1s).
 *
 * It replaces a tool that waited for a selection (one greyed, unlabelled mark), four
 * unlabelled arrangement marks, sliders with only an icon in front of them, and effectors
 * on a second strip behind a breadcrumb. Here everything is said in words: Grid, Row,
 * Circle, Along a Path; Across, Down, Gap; Vary: Scatter, Grow, Twist, Fade, Magnify, each
 * with one Amount. The engine is the same cloner and effectors (`tools/repeat.ts`); every
 * other setting of a variation is one press away, on the Cloner tool's strip.
 */

type Arrangement = 'grid' | 'linear' | 'radial' | 'path';

export interface RepeatActions {
  /** Wraps what is chosen in a repeat and chooses it. */
  repeat(): void;
  /** Sets a repeat's or a variation's own numbers. */
  set(id: string, props: Record<string, unknown>): void;
  vary(clonerId: string, recipe: (typeof RECIPES)[number]['id']): void;
  unvary(effectorId: string): void;
  /** Opens a variation on the Cloner tool's strip, where all its settings are. */
  openVariation(effectorId: string): void;
  makeLayers(clonerId: string): void;
  stop(clonerId: string): void;
  node(id: string): NodeRecord | undefined;
  /** The size of what a repeat copies, in its own space: what its Gap is measured from. */
  source(clonerId: string): { width: number; height: number } | null;
  /** Shapes on the page a repeat could run along, by name. */
  paths(clonerId: string): { id: string; name: string }[];
}

type Cloner = Extract<NodeRecord, { type: 'cloner' }>;

/** The repeat a selection is part of: the chosen repeat itself, or the one holding what is chosen. */
export function repeatOf(nodes: readonly NodeRecord[], node: (id: string) => NodeRecord | undefined): Cloner | null {
  for (const one of nodes) {
    if (one.type === 'cloner') return one;
    const parent = (one as { parentId?: string }).parentId;
    const holder = parent ? node(parent) : undefined;
    if (holder?.type === 'cloner') return holder;
  }
  return null;
}

const ARRANGEMENTS: { id: Arrangement; name: string; art: ReactNode }[] = [
  { id: 'grid', name: 'Grid', art: dots([[9, 6], [17, 6], [25, 6], [9, 14], [17, 14], [25, 14]]) },
  { id: 'linear', name: 'Row', art: dots([[5, 10], [13, 10], [21, 10], [29, 10]]) },
  { id: 'radial', name: 'Circle', art: dots(Array.from({ length: 8 }, (_, k) => [17 + Math.cos((k / 8) * Math.PI * 2) * 7, 10 + Math.sin((k / 8) * Math.PI * 2) * 7])) },
  { id: 'path', name: 'Along a Path', art: dots(Array.from({ length: 6 }, (_, k) => [4 + (k / 5) * 26, 10 + Math.sin((k / 5) * Math.PI * 2) * 5.5])) },
];

function dots(points: number[][]): ReactNode {
  return (
    <svg viewBox="0 0 34 20" className="h-[20px] w-[34px]" aria-hidden>
      {points.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={2.4} fill="currentColor" />
      ))}
    </svg>
  );
}

export function RepeatPanel({ cloner, actions, embedded = false }: { cloner: Cloner; actions: RepeatActions; embedded?: boolean }) {
  const source = actions.source(cloner.id) ?? { width: 0, height: 0 };
  const [pathsAnchor, pathsRef] = useAnchor<HTMLButtonElement>();
  const [choosingPath, setChoosingPath] = useState(false);
  // Asked once per repeat and again as the menu opens, never per render: an Amount being
  // dragged redraws this on every step, and the list walks the whole page.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const paths = useMemo(() => actions.paths(cloner.id), [cloner.id, choosingPath]);
  const set = (props: Record<string, unknown>) => actions.set(cloner.id, props);
  // The gap between copies, which is what a person thinks in; the engine keeps the step
  // from one copy's middle to the next.
  const gap = Math.round(cloner.stepX - source.width);
  const setGap = (value: number) => set(cloner.mode === 'grid' ? { stepX: Math.round(source.width + value), stepY: Math.round(source.height + value) } : { stepX: Math.round(source.width + value) });

  const arrange = (to: Arrangement) => {
    if (to === cloner.mode) return;
    if (to === 'path') {
      setChoosingPath(true);
      return;
    }
    // A row runs straight across, with the gap it had; a grid and a ring keep theirs.
    if (to === 'linear') set({ mode: 'linear', stepX: Math.round(source.width + Math.max(gap, source.width * 0.25)), stepY: 0 });
    else set({ mode: to });
  };

  const field = (label: string, value: number, onChange: (value: number) => void, min: number, max: number, suffix?: string) => (
    <NumberField className="min-w-0 flex-1" label={label} prefix={label} prefixWidth={label.length * 6.6 + 10} value={value} min={min} max={max} decimals={0} suffix={suffix} onChange={onChange} />
  );


  return (
    <div className={`grid grid-cols-[minmax(0,1fr)] gap-[10px] ${embedded ? 'inspector-repeat' : 'p-[12px]'}`}>
      {!embedded && <div className="flex items-center gap-[4px]">
        <strong className="min-w-0 flex-1 text-[13px]">Cloner</strong>
        <button type="button" onClick={() => actions.makeLayers(cloner.id)} {...tip('Make Layers — the copies become ordinary layers, each its own')} className="h-[26px] rounded-[8px] px-[8px] text-[12px] text-label hover:bg-fill-1">
          Make Layers
        </button>
        <button type="button" onClick={() => actions.stop(cloner.id)} {...tip('Remove cloner — keep the original objects')} className="h-[26px] rounded-[8px] px-[8px] text-[12px] text-label hover:bg-fill-1">
          Remove cloner
        </button>
      </div>}

      <div role="radiogroup" aria-label="Arrangement" className="grid grid-cols-4 gap-[6px]">
        {ARRANGEMENTS.filter((one) => one.id !== 'path' || paths.length > 0 || cloner.mode === 'path').map((one) => {
          const on = cloner.mode === one.id;
          return (
            <button
              key={one.id}
              ref={one.id === 'path' ? pathsRef : undefined}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => arrange(one.id)}
              className={`grid justify-items-center gap-[3px] rounded-[10px] px-[4px] pb-[6px] pt-[8px] text-[11px] font-medium leading-[13px] transition-colors ${on ? 'bg-accent-soft text-accent-text' : 'bg-fill-2 text-label-2 hover:bg-fill-1 hover:text-label'}`}
            >
              {one.art}
              {one.name}
            </button>
          );
        })}
      </div>
      <Menu
        open={choosingPath}
        anchor={pathsAnchor}
        onClose={() => setChoosingPath(false)}
        placement="above"
        label="Run the copies along"
        items={paths.map((path) => ({ id: path.id, label: path.name, checked: cloner.pathId === path.id, onSelect: () => set({ mode: 'path', pathId: path.id }) }))}
      />

      <div className={embedded ? 'grid grid-cols-2 gap-[6px]' : 'flex gap-[6px]'}>
        {cloner.mode === 'grid' ? (
          <>
            {field('Across', cloner.countX, (countX) => set({ countX: Math.max(1, Math.round(countX)) }), 1, 200)}
            {field('Down', cloner.countY, (countY) => set({ countY: Math.max(1, Math.round(countY)) }), 1, 200)}
            {field('Gap', gap, setGap, -10_000, 10_000)}
          </>
        ) : cloner.mode === 'linear' ? (
          <>
            {field('Copies', cloner.count, (count) => set({ count: Math.max(1, Math.round(count)) }), 1, 500)}
            {field('Gap', gap, setGap, -10_000, 10_000)}
            {field('Turn', cloner.stepRotation, (stepRotation) => set({ stepRotation }), -360, 360, '°')}
          </>
        ) : cloner.mode === 'radial' ? (
          <>
            {field('Copies', cloner.count, (count) => set({ count: Math.max(1, Math.round(count)) }), 1, 500)}
            {field('Size', cloner.radius, (radius) => set({ radius: Math.max(1, radius) }), 1, 100_000)}
            {field('Sweep', cloner.endAngle - cloner.startAngle, (sweep) => set({ endAngle: cloner.startAngle + Math.max(1, Math.min(360, sweep)) }), 1, 360, '°')}
          </>
        ) : (
          <>
            {field('Copies', cloner.count, (count) => set({ count: Math.max(1, Math.round(count)) }), 1, 500)}
            <button
              type="button"
              aria-pressed={cloner.follow}
              onClick={() => set({ follow: !cloner.follow })}
              className={`h-[26px] min-w-0 flex-1 truncate rounded-[8px] px-[8px] text-[12px] ${cloner.follow ? 'bg-accent-soft text-accent-text' : 'bg-fill-2 text-label'}`}
            >
              Turn with the Path
            </button>
          </>
        )}
      </div>

      {embedded && <div className="grid grid-cols-2 gap-[6px]">
        {(cloner.mode === 'grid' || cloner.mode === 'linear') && <>
          {field('Step X', cloner.stepX, stepX => set({ stepX }), -100_000, 100_000)}
          {field('Step Y', cloner.stepY, stepY => set({ stepY }), -100_000, 100_000)}
        </>}
        {cloner.mode === 'radial' && field('Start', cloner.startAngle, startAngle => set({ startAngle }), -360, 360, '°')}
        {cloner.mode === 'path' && <NumberField label="Path spread" prefix="Spread" prefixWidth={54} value={Math.round(cloner.spread * 100)} suffix="%" min={-100} max={100} onChange={value => set({ spread: value / 100 })} />}
      </div>}

      <RepeatVariations cloner={cloner} actions={actions} />
    </div>
  );
}

/** The variation controls are shared with their own toolbar popover. */
export function RepeatVariations({ cloner, actions, showHeading = true }: { cloner: Cloner; actions: RepeatActions; showHeading?: boolean }) {
  const effectors = (cloner.effectors ?? []).map((id) => actions.node(id)).filter((one): one is Extract<NodeRecord, { type: 'effector' }> => one?.type === 'effector');
  const named = new Set(effectors.map((one) => one.name));

  return (
    <div role="group" aria-label="Variations" className="grid gap-[8px]">
      {showHeading && <span className="text-[11px] font-medium text-label-2">Variations{effectors.length ? ` · ${effectors.length}` : ''}</span>}
      <div className="flex flex-wrap gap-[5px]">
        {RECIPES.map((recipe) => {
          const on = named.has(recipe.name);
          return (
            <button
              key={recipe.id}
              type="button"
              aria-pressed={on}
              {...tip(recipe.title)}
              onClick={() => {
                const made = effectors.find((one) => one.name === recipe.name);
                if (made) actions.unvary(made.id);
                else actions.vary(cloner.id, recipe.id);
              }}
              className={`h-[28px] rounded-full px-[11px] text-[12px] font-medium transition-colors ${on ? 'bg-accent-soft text-accent-text' : 'bg-fill-1 text-label hover:bg-fill-selected'}`}
            >
              {recipe.name}
            </button>
          );
        })}
      </div>
      {effectors.length > 0 ? (
        <div className="grid grid-cols-[minmax(0,1fr)] gap-[2px]">
          {effectors.map((one) => (
            <div key={one.id} className="grid grid-cols-[72px_minmax(0,1fr)_40px_22px] items-center gap-[6px]">
              <button type="button" onClick={() => actions.openVariation(one.id)} {...tip(`${one.name} — all its settings: how far it moves, turns, sizes and shades, and where`)} className="truncate text-left text-[12px] text-label hover:underline">
                {one.name || 'Variation'}
              </button>
              <Slider label={`${one.name} amount`} value={Math.round(one.strength * 100)} min={0} max={200} onChange={(value) => actions.set(one.id, { strength: value / 100 })} />
              <span className="tnum text-right text-[12px] text-label-2">{Math.round(one.strength * 100)}%</span>
              <button type="button" aria-label={`Remove ${one.name}`} {...tip(`Remove ${one.name}`)} onClick={() => actions.unvary(one.id)} className="grid size-[22px] place-items-center rounded-[6px] text-[15px] leading-none text-label-3 hover:bg-fill-1 hover:text-label">
                ×
              </button>
            </div>
          ))}
        </div>
      ) : null}
      </div>
  );
}
